import { useEffect, useState } from "react";
import { Check, ChevronUp } from "lucide-react";
import { OCCASIONS, HOME_TYPES, SIZES, STOVES } from "@/lib/content";
import { WINDOWS } from "@/lib/booking";
import { priceSummary, usd, BASE_PRICE, DEPOSIT } from "@/lib/addons";

const lines = (stove) => ["Laboratory water testing", "Seven-day bedroom air monitoring", stove === "gas" ? "Gas-cooking NO₂ screening" : null, "Room-by-room walkthrough", "Written findings and a 45-minute review"].filter(Boolean);
const label = (list, id) => list.find((x) => x.id === id)?.label;

const Row = ({ k, v, strong, testId }) => (
  <div className="flex justify-between gap-4 text-sm"><dt className={strong ? "text-ink font-medium" : "text-sec"}>{k}</dt><dd className={`text-right font-mono ${strong ? "text-forest font-medium" : "text-ink"}`} data-testid={testId}>{v}</dd></div>
);

const Body = ({ data, compact }) => {
  const p = priceSummary(data.addons);
  return (
    <>
      <dl className="mt-4 space-y-2" data-testid="summary-lines">
        <Row k="Home Hyper Assessment" v={usd(BASE_PRICE)} testId="summary-base" />
        {p.addons.map((a) => <Row key={a.id} k={a.name} v={`+${usd(a.price)}`} testId={`summary-addon-${a.id}`} />)}
        <div className="rule pt-2"><Row k="Total assessment value" v={usd(p.totalValue)} testId="summary-total-value" /></div>
        <Row k="Due today" v={usd(p.dueToday)} strong testId="summary-due-today" />
      </dl>
      <dl className="mt-4 rounded-md bg-mint px-4 py-3 space-y-1.5" data-testid="summary-breakdown">
        <Row k="Deposit" v={usd(DEPOSIT)} testId="summary-deposit" />
        <Row k="Add-ons charged today" v={usd(p.addonTotal)} testId="summary-addons-today" />
        <div className="flex justify-between gap-4 text-sm"><dt className="text-sec">Remaining assessment balance</dt><dd className="text-right text-ink" data-testid="summary-remaining"><span className="font-mono">{usd(p.remaining)}</span> <span className="text-sec">due 24 hours before visit</span></dd></div>
      </dl>
      {!compact && (
        <>
          <ul className="mt-5 rule pt-4 space-y-2" data-testid="summary-inclusions">
            {lines(data.stove).map((t) => <li key={t} className="flex items-start gap-2.5 text-sm text-ink"><span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-forest text-white"><Check size={10} strokeWidth={3} /></span>{t}</li>)}
          </ul>
          {data.stove && data.stove !== "gas" && <p className="mt-3 text-xs text-sec leading-relaxed" data-testid="summary-stove-note">Stove emissions step doesn't apply to {label(STOVES, data.stove).toLowerCase()} cooking. Price is unchanged.</p>}
          <dl className="mt-5 rule pt-4 space-y-1.5">
            {[["ZIP", data.zip || "—"], ["Home", [label(HOME_TYPES, data.homeType), label(SIZES, data.size)].filter(Boolean).join(" · ") || "—"], ["Reason", label(OCCASIONS, data.reason) || "—"], ["Preferred visit", data.dateLabel ? `${data.dateLabel}${data.window ? ` · ${label(WINDOWS, data.window)}` : ""}` : "—"]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 text-sm"><dt className="text-sec">{k}</dt><dd className="text-right text-ink font-medium" data-testid={`summary-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd></div>
            ))}
          </dl>
        </>
      )}
    </>
  );
};

export const Summary = ({ data }) => (
  <aside className="hidden lg:block rounded-md border border-divider bg-white p-7" data-testid="order-summary" aria-label="Order summary">
    <p className="mono uppercase tracking-[0.16em] text-sec">Your assessment</p>
    <h2 className="h-sub mt-1.5">Home Hyper Assessment</h2>
    <Body data={data} />
  </aside>
);

export const MobileSummaryBar = ({ data, hidden }) => {
  const [open, setOpen] = useState(false);
  const p = priceSummary(data.addons);
  useEffect(() => {
    document.body.classList.toggle("has-booking-bar", !hidden);
    return () => document.body.classList.remove("has-booking-bar");
  }, [hidden]);
  if (hidden) return null;
  return (
    <>
      {open && <button type="button" aria-label="Close summary" onClick={() => setOpen(false)} className="lg:hidden fixed inset-0 z-40 bg-ink/30" data-testid="summary-backdrop" />}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-50 border-t border-divider bg-white shadow-[0_-6px_24px_-12px_rgba(22,59,48,0.2)] safe-bottom" data-testid="mobile-summary-bar">
        {open && (
          <div className="container-x max-h-[60vh] overflow-y-auto pt-4 pb-2 reveal" data-testid="mobile-summary-body">
            <p className="mono uppercase tracking-[0.16em] text-sec">Your assessment</p>
            <Body data={data} compact />
          </div>
        )}
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="container-x flex w-full items-center justify-between gap-4 py-3 min-h-[56px] text-left" data-testid="summary-toggle">
          <span>
            <span className="block text-xs text-sec">Due today</span>
            <span className="block font-mono text-lg font-medium text-forest leading-tight" data-testid="mobile-due-today">{usd(p.dueToday)}</span>
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-forest">
            {p.addons.length ? `${p.addons.length} add-on${p.addons.length > 1 ? "s" : ""}` : "Details"}
            <ChevronUp size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </span>
        </button>
      </div>
    </>
  );
};
